import { HttpClient, HttpHeaders } from "@angular/common/http";
import {inject, Injectable} from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { filter } from 'rxjs/operators';
import { AppSettings } from "../app.settings";
import { DataPlateService } from "../dataplate/dataplate.service";
import { SharedInfoService } from "../partsCatalog/sharedInfo.service";
import {TranslateService} from "@ngx-translate/core";

@Injectable({ providedIn: 'root' })
export class SbomService {
    private _loaderEvent: BehaviorSubject<DownloadEvent> = new BehaviorSubject<DownloadEvent>(null);
    private readonly translate = inject(TranslateService);

    constructor(private readonly _httpClient: HttpClient,
        private readonly dataplateservice: DataPlateService) {
        this.setBlob = this.setBlob.bind(this);
        this.setErrorResponse = this.setErrorResponse.bind(this);
    }

    get loaderEvent$(): Observable<DownloadEvent> {
        return this._loaderEvent.pipe(filter(event => !!event));
    }

    newDownloadSbom(_sharedService: SharedInfoService) {
        this._loaderEvent.next({ type: "pending" });
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const res = _sharedService.currentEsnInfo;
        const url = `${AppSettings.API_ENDPOINTS.get('IACDataServices')}/v2/esnInfo/${res.esn}/sbom?esnType=${res.mbom}`;
        return this._httpClient.get(url, { headers, responseType: 'blob' }).toPromise()
            .then(this.setBlob).catch(this.setErrorResponse);
    }

    public async downloadSbom(_sharedService: SharedInfoService): Promise<any> {
        if (await this.dataplateservice.isRestructureFlagEnabled(_sharedService.currentEsnInfo.mbom)) {
            return this.newDownloadSbom(_sharedService);
        }

        this._loaderEvent.next({ type: "pending" });
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const res = _sharedService.currentEsnInfo;
        const url = `${AppSettings.API_ENDPOINTS.get('IACDataServices')}/getSbom?esn=${res.esn}`;
        const preCheck = res.buildDate ? new Date(res.buildDate).getFullYear() : 2014;
        const sbomParameters = {
            "engineBuildDate": res.buildDate,
            "esn": res.esn
        };

        if (preCheck >= 2015) {
            if (res.kitSets) {
                sbomParameters['kitSets'] = res.kitSets;
            } else {
                let engineType = (res.mbom === "mbom" ? "mbom_" : (res.mbom === "package" ? "package_" : "")) + "options";
                sbomParameters['optionList'] = res.optionList;
                sbomParameters['cplNbr'] = res.cpl;
                sbomParameters['enginePlantCode'] = res.enginePlantCode;
                sbomParameters['engineType'] = engineType;
            }

            if (res.gasketSets) {
                sbomParameters['gasketSets'] = res.gasketSets;
            }
            else {
                sbomParameters['configuration'] = res.marketingConfig;
            }
        }

        return this._httpClient.post(url, JSON.stringify(sbomParameters), { headers, responseType: 'blob' }).toPromise()
            .then((this.setBlob)).catch(this.setErrorResponse);
    }

    setBlob(blob: Blob) {
        if ((window.navigator as any).msSaveOrOpenBlob) {
            (window.navigator as any).msSaveOrOpenBlob(blob, 'serviceBOM.xlsx');
        } else {
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.style.visibility = 'hidden';
            anchor.download = 'serviceBOM.xlsx';
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
        }
        this._loaderEvent.next({ type: "done" });
    }

    async setErrorResponse(errorResponse: any) {
        console.error("Error downloading SBOM:", errorResponse);
        this.translate.get('sbom.downloadError').subscribe((message: string) => {
          this._loaderEvent.next({
            type: "error",
            message
          })
        });
    }
}

export interface DownloadEvent {
    type: 'pending' | 'done' | 'error';
    message?: any;
}
